<template>
    <v-list class="py-0">
        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">Hotel</v-list-item-title>
                <v-list-item-subtitle>
                    <div class="d-flex align-items-center flex-wrap mb-1">
                        <a
                            :href="hotelSinglePageUrl"
                            target="_blank"
                            class="text-subtitle-2"
                        >
                            {{ event.checkout_data.hotelName }}
                        </a>
                        <rating-stars
                            v-if="event.checkout_data.hotelStarRating"
                            :rating="Number(event.checkout_data.hotelStarRating)"
                            :show-only-full-stars="true" color="#F59E0B"
                            class="ml-2"
                        />
                    </div>
                    <span>
                        <template v-if="event.checkout_data.hotelAddress">{{ event.checkout_data.hotelAddress }}</template>
                        <template v-if="event.checkout_data.hotelCity">, {{ event.checkout_data.hotelCity }}</template>
                        <template v-if="event.checkout_data.hotelCountry">, {{ event.checkout_data.hotelCountry }}</template>
                    </span>
                </v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Price') }}</v-list-item-title>
                <v-list-item-subtitle class="font-weight-bold">
                    <template v-if="event.excludedTaxesAndFees">
                        <div>
                            <span>{{ $t('Paid') }}:</span>
                            {{ getCurrencySymbol(event.currency) }} {{ event.priceWithTax }}
                        </div>
                        <div>
                            <span>{{ $t('Pay on arrival') }}:</span>
                            {{ getCurrencySymbol(event.currency) }} {{ event.excludedTaxesAndFees }}
                        </div>
                    </template>
                    <template v-else>{{ getCurrencySymbol(event.currency) }} {{ event.priceWithTax }}</template>
                </v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Form Property Guests') }}</v-list-item-title>
                <v-list-item-subtitle class="mb-3">
                    {{ $tc('adults_count', event.adults_count) }}
                    <template v-if="event.children_count">, {{ $tc('children_count', event.children_count) }}</template>
                </v-list-item-subtitle>
                <div v-for="(roomGuest, index) in event.guests" :key="index">
                    <v-card
                        outlined
                        :class="{'mt-2': index}"
                    >
                        <v-card-title class="text-subtitle-2">
                            {{ $t('Room') }} {{ index + 1 }}
                            <template
                                v-if="
                                    event.checkout_data.rateRoomDetails
                                    && (
                                        event.checkout_data.rateRoomDetails[index]
                                        || event.checkout_data.rateRoomDetails[0]
                                    )
                                "
                            >
                                - {{ (event.checkout_data.rateRoomDetails[index] || event.checkout_data.rateRoomDetails[0]).name }}
                            </template>
                        </v-card-title>
                        <v-card-text>
                            <div>{{ $t('Full name') }}: <b>{{ roomGuest.firstName }} {{ roomGuest.lastName }}</b></div>
                            <div>{{ $t('Email') }}: <b>{{ roomGuest.email }}</b></div>
                            <div>{{ $t('Phone') }}: <b>{{ roomGuest.phone }}</b></div>
                        </v-card-text>
                    </v-card>
                </div>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Nights') }}</v-list-item-title>
                <v-list-item-subtitle>{{ event.nightsCount }}</v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item
            v-if="event.checkout_data.rateRoomDetails"
            two-line
            class="pl-0 my-2"
        >
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Rooms') }}</v-list-item-title>
                <v-list-item-subtitle class="d-flex flex-column">
                    <div
                        v-for="(room, index) in event.checkout_data.rateRoomDetails"
                        :key="index"
                        :class="{'mt-2': index}"
                    >
                        {{ room.count }} x {{ room.name }}
                    </div>
                </v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Check-in') }}</v-list-item-title>
                <v-list-item-subtitle>{{fullDateFormat(event.checkin)}}</v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Check-out') }}</v-list-item-title>
                <v-list-item-subtitle>{{fullDateFormat(event.checkout)}}</v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Booking date') }}</v-list-item-title>
                <v-list-item-subtitle>{{fullDateFormat(event.created_at, true)}}</v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">Status</v-list-item-title>
                <v-list-item-subtitle>
                    <v-chip :color="event.cancelled ? 'red white--text' : ''">
                        {{ $t(`AdFormStatus.${event.status}`) }}
                    </v-chip>
                </v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Cancellation policy') }}</v-list-item-title>
                <v-list-item-subtitle class="mt-2">
                    <hotel-rate-cancellation-policy :data="event"/>
                </v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>

        <template v-if="event.lastFreeCancellationDate">
            <v-divider></v-divider>

            <v-list-item two-line class="pl-0 my-2">
                <v-list-item-content>
                    <v-list-item-title class="mb-2">{{ $t('Free cancellation deadline') }}</v-list-item-title>
                    <v-list-item-subtitle>{{fullDateFormat(event.lastFreeCancellationDate, true)}}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </template>

        <template v-if="event.amountRefunded">
            <v-divider></v-divider>

            <v-list-item two-line class="pl-0 my-2">
                <v-list-item-content>
                    <v-list-item-title class="mb-2">{{ $t('Refunded') }}</v-list-item-title>
                    <v-list-item-subtitle>{{ getCurrencySymbol(event.currency) }} {{ event.amountRefunded }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </template>

        <v-divider></v-divider>

        <v-list-item two-line class="pl-0 my-2">
            <v-list-item-content>
                <v-list-item-title class="mb-2">{{ $t('Reservation code') }}</v-list-item-title>
                <v-list-item-subtitle>{{event.booking_reference}}</v-list-item-subtitle>
            </v-list-item-content>
        </v-list-item>
    </v-list>
</template>

<script>
import HotelRateCancellationPolicy from '@/components/reservations/HotelRateCancellationPolicy.vue';
import RatingStars from '@/components/RatingStars.vue';
import CurrencyMixin from '@/lib/CurrencyMixin';
import { fullDateFormat } from '@/components/calendar/config';

export default {
  components: { HotelRateCancellationPolicy, RatingStars },
  mixins: [CurrencyMixin],
  props: {
    event: {
      required: true,
      type: Object,
    },
  },
  computed: {
    hotelSinglePageUrl() {
      return `${process.env.VUE_APP_APP_URL}/hotel/${this.event.hotelId}`;
    },
  },
  methods: {
    fullDateFormat,
  },
};
</script>
